/**
 * OAuth 2.1 + PKCE sign-in against lsgo-authentication ("every sign-in starts
 * at the app"). Ported from lsgo-teacher's util of the same name, without its
 * localStorage testing flag. Generates a fresh PKCE pair per attempt, so it is
 * safe to call from any sign-in entry point: /auth/start, a legacy sign-in
 * refused with AUTH_MANAGED (utils/lsgoAuthenticationSchools.js) and Sentral
 * SSO for schools on auth (containers/Student/SentralLogin.js).
 */

import {
    LSGO_AUTHENTICATION_BASE_URL,
    LSGO_AUTHENTICATION_CLIENT_ID,
    LSGO_AUTHENTICATION_REDIRECT_URI,
} from 'config/lsgoAuthentication';

// Per-environment values live in config/lsgoAuthentication.js.
export const AUTH_BASE_URL = LSGO_AUTHENTICATION_BASE_URL;
export const CLIENT_ID = LSGO_AUTHENTICATION_CLIENT_ID;
export const REDIRECT_URI = LSGO_AUTHENTICATION_REDIRECT_URI;

const PENDING_KEY_PREFIX = 'lsgo_new_auth_pending:';
const DEFAULT_RETURN_PATH = '/';

function base64url(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function randomBase64url(byteLength) {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    return base64url(bytes);
}

export async function codeChallengeFor(verifier) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64url(new Uint8Array(digest));
}

/**
 * Where a sign-in may return to: only a plain same-origin path. Anything else
 * (absolute or protocol-relative URLs, backslashes browsers fold to "/") falls
 * back to the student home. The return path is later handed to history.push by
 * LsgoAuthenticationCallback.
 */
export function safeReturnPath(raw) {
    if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) {
        return DEFAULT_RETURN_PATH;
    }
    return raw;
}

/**
 * Starts an OAuth 2.1 + PKCE sign-in against lsgo-authentication, ending back
 * at `returnPath` once LsgoAuthenticationCallback.js completes the exchange.
 * The verifier and the return path are kept in sessionStorage under a fresh
 * `state` nonce, so nothing about the return path is exposed in the URL.
 *
 * `tenant` (lsgo_school_<id>) preselects the school; `idp` (e.g. 'sentral')
 * goes straight to that school's provider instead of the sign-in page.
 */
export async function startLsgoAuthenticationLogin(returnPath, { idp, tenant } = {}) {
    const verifier = randomBase64url(32);
    const challenge = await codeChallengeFor(verifier);
    const state = randomBase64url(16);

    sessionStorage.setItem(
        `${PENDING_KEY_PREFIX}${state}`,
        JSON.stringify({ verifier, returnPath: safeReturnPath(returnPath) })
    );

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        code_challenge: challenge,
        code_challenge_method: 'S256',
        state,
        scope: 'openid profile',
    });
    if (idp) params.set('idp', idp);
    if (tenant) params.set('tenant', tenant);
    window.location.href = `${AUTH_BASE_URL}/authorize?${params.toString()}`;
}

/**
 * Reads back and consumes (single use) the verifier + return path stored by
 * startLsgoAuthenticationLogin for a given `state`. Returns null if there is
 * no matching entry: a reload of the callback URL, a tampered `state`, or a
 * flow that did not start in this browser tab.
 */
export function consumePendingLogin(state) {
    const key = `${PENDING_KEY_PREFIX}${state}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}
